import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import type { Article } from "@/generated/sb";
import { useDebounce } from "@repo/utils/hooks/useDebounce";
import { fetchArticles, fetchFolders, fetchStories } from "@/lib/storyblok/api";
import {
  PrefetchedArticlesData,
  FilterOptionData,
} from "@/types/articles";
import { useGlobalDataContext } from "@/components/GlobalDataContext";
import { ISbStoryData } from "storyblok-js-client";

export interface FilterOption {
  label: string;
  value: string;
}

export interface Filter {
  key: string;
  label: string;
  value: string;
  multiselect: boolean;
  renderType: "select";
  onValueChange: (value: string) => void;
  options: FilterOption[];
}

interface FilterGroup {
  key: string;
  label: string;
  options: FilterOptionData[];
  position: number;
}

function getDateStringForFilter(value: string) {
  const now = new Date();
  const date = new Date(now);

  switch (value) {
    case "30d":
      date.setDate(now.getDate() - 30);
      break;
    case "90d":
      date.setDate(now.getDate() - 90);
      break;
    case "6m":
      date.setMonth(now.getMonth() - 6);
      break;
    case "1y":
      date.setFullYear(now.getFullYear() - 1);
      break;
    case "all":
      return null;
    default:
      return null;
  }

  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function useArticles(
  preselectedArticles: (ISbStoryData<Article> | string)[] = [],
  options?: {
    onFilterChange?: (filterKey: string, selectedTitle: string, selectedValue: string) => void;
    onSearchChange?: (searchQuery: string) => void;
    specificArticleType?: string;
    prefetchedData?: PrefetchedArticlesData;
    currentPage?: number;
    initialFilters?: Record<string, string>;
    initialSearch?: string;
  },
) {
  const { domain, version } = useGlobalDataContext();

  const prefetchedArticlesData = options?.prefetchedData;

  const usedPrefetchedData = useRef(false);

  const initialFilters: FilterGroup[] =
    prefetchedArticlesData?.filters?.map((fg) => ({
      key: fg.key,
      label: fg.label,
      options: fg.options,
      position: fg.position,
    })) || [];

  const initialArticles: ISbStoryData<Article>[] =
    (prefetchedArticlesData?.articles as ISbStoryData<Article>[]) ||
    [];

  const [filterGroups, setFilterGroups] =
    useState<FilterGroup[]>(initialFilters);
  const [filterValues, setFilterValues] = useState<Record<string, string>>(
    options?.initialFilters || {},
  );
  const [searchQuery, setSearchQuery] = useState<string>(
    options?.initialSearch || "",
  );

  const [articles, setArticles] =
    useState<ISbStoryData<Article>[]>(initialArticles);
  const [isLoading, setIsLoading] = useState(false);
  const perPage = 8;
  const [totalPages, setTotalPages] = useState(
    prefetchedArticlesData
      ? Math.ceil(prefetchedArticlesData.total / prefetchedArticlesData.perPage)
      : 1,
  );

  const currentPage = options?.currentPage || 1;

  const initialHasNextPage = prefetchedArticlesData
    ? prefetchedArticlesData.total > prefetchedArticlesData.perPage
    : true;
  const [hasNextPage, setHasNextPage] = useState(initialHasNextPage);

  const initialFilterQuery = useMemo(() => {
    const query: Record<string, Record<string, string | null> | any> = {};

    if (options?.specificArticleType) {
      query.type = { in: options.specificArticleType };
    }

    if (options?.initialFilters && filterGroups.length > 0) {
      Object.entries(options.initialFilters).forEach(([key, titleFromUrl]) => {
        if (titleFromUrl) {
          const filterGroup = filterGroups.find((group) => group.key === key);
          if (filterGroup) {
            const option = filterGroup.options.find((opt) => opt.title === titleFromUrl);
            if (option) {
              if (key.toLowerCase() === "when" && option.value) {
                const dateString = getDateStringForFilter(option.value);
                if (dateString) {
                  query.publicationDate = { gt_date: dateString };
                }
              } else {
                query[key.toLowerCase()] = { in: option.uuid };
              }
            }
          }
        }
      });
    }

    return query;
  }, [options?.specificArticleType, options?.initialFilters, filterGroups]);

  const [filterQuery, setFilterQuery] =
    useState<Record<string, Record<string, string | null> | any>>(
      initialFilterQuery,
    );

  const preselectedUuids = preselectedArticles
    .filter((article) => typeof article !== "string")
    .map((article) => article.uuid);

  useEffect(() => {
    setFilterQuery(initialFilterQuery);
  }, [initialFilterQuery]);

  const fetchAllFilters = async () => {
    try {
      const rootFilterPath = `${domain}/global-components/filters/`;
      const filterFoldersResponse = await fetchStories({
        version: version,
        params: {
          starts_with: rootFilterPath,
          sort_by: "position:asc",
        },
      });

      const folders = await fetchFolders({
        version: version,
        params: { starts_with: rootFilterPath },
      });

      const loadedFilterGroups: FilterGroup[] = [];

      for (const folder of folders) {
        const folderName = folder.name!;
        const folderKey = folder.slug?.split("/").pop() || folderName;

        const relatedOptions = filterFoldersResponse.data.stories.filter(
          (story) => story.parent_id === folder.id,
        );

        const mappedOptions = relatedOptions
          .filter((option) => option.content.component !== "filterConfig")
          .map((option) => ({
            uuid: option.uuid,
            title: option.content.title,
            value: option.content.value,
          }))
          .sort((a, b) => {
            const aStory = relatedOptions.find(
              (story) => story.uuid === a.uuid,
            );
            const bStory = relatedOptions.find(
              (story) => story.uuid === b.uuid,
            );
            return (aStory?.position || 0) - (bStory?.position || 0);
          });

        if (mappedOptions.length > 0) {
          loadedFilterGroups.push({
            key: folderKey,
            label: folderName,
            options: mappedOptions,
            position: folder.position || 0,
          });

          setFilterValues((prev) => ({ ...prev, [folderKey]: "" }));
        }
      }

      const orderedFilterGroups = loadedFilterGroups.sort(
        (a, b) => a.position - b.position,
      );

      setFilterGroups(orderedFilterGroups);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  useEffect(() => {
    if (
      prefetchedArticlesData?.filters &&
      prefetchedArticlesData.filters.length > 0
    ) {
      return;
    }
    if (domain && version) {
      fetchAllFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, version]);

  const filterQueryBuilderByKey = useCallback((key: string, value: string) => {
    if (!value) return {};

    // Special handling for date filters
    if (key.toLowerCase() === "when") {
      const dateString = getDateStringForFilter(value);
      return {
        publicationDate: { gt_date: dateString },
      };
    }

    const filterQuery = {
      [key.toLowerCase()]: {
        in: value,
      },
    };

    return filterQuery;
  }, []);

  const createSetValue = useCallback(
    (key: string) => (uuidValue: string) => {
      const filterGroup = filterGroups.find((group) => group.key === key);
      const option = filterGroup?.options.find((opt) => opt.uuid === uuidValue);
      const selectedTitle = option?.title || "";

      const prevValue = filterValues[key] || "";
      setFilterValues((prev) => ({ ...prev, [key]: selectedTitle }));

      if (selectedTitle !== prevValue) {
        options?.onFilterChange?.(key, selectedTitle, uuidValue);
        usedPrefetchedData.current = false;
      }

      setFilterQuery((currentQuery) => {
        let updatedFilters;
        if (!uuidValue) {
          const filterKey = key.toLowerCase();
          const { [filterKey]: _, publicationDate: __, ...rest } = currentQuery || {};
          updatedFilters = rest;
        } else {
          const filterValue =
            key.toLowerCase() === "when" && option?.value
              ? option.value
              : uuidValue;
          const query = filterQueryBuilderByKey(key, filterValue);
          updatedFilters = { ...currentQuery, ...query };
        }

        if (options?.specificArticleType && !updatedFilters.type) {
          updatedFilters = {
            ...updatedFilters,
            type: { in: options.specificArticleType },
          };
        }

        return updatedFilters;
      });
    },
    [filterQueryBuilderByKey, filterGroups, options, searchQuery, filterValues],
  );

  const debouncedSearch = useDebounce((query: string, prevQuery: string) => {
    if (query !== prevQuery) {
      options?.onSearchChange?.(query);
    }
  }, 600);

  const handleSearchChange = useCallback(
    (query: string) => {
      const prevQuery = searchQuery;
      setSearchQuery(query);
      debouncedSearch(query, prevQuery);
      usedPrefetchedData.current = false;
    },

    [debouncedSearch, searchQuery],
  );

  const getFilterValue = useCallback(
    (key: string) => {
      const title = filterValues[key] || "";
      if (!title) return "";

      const filterGroup = filterGroups.find((group) => group.key === key);
      if (!filterGroup) return "";

      const option = filterGroup.options.find((opt) => opt.title === title);
      return option?.uuid || "";
    },
    [filterValues, filterGroups],
  );

  const fetchArticlesPage = async ({
    page,
    perPage,
    filterQuery,
    searchTerm,
  }: {
    page: number;
    perPage: number;
    filterQuery:
      | Record<string, Record<string, string | null> | any>
      | undefined;
    searchTerm?: string;
  }) => {
    try {
      setIsLoading(true);
      const response = await fetchArticles({
        version,
        domain,
        perPage,
        page,
        filterQuery,
        sortBy: "content.publicationDate:desc",
        excludeUuids: preselectedUuids,
        searchTerm,
      });

      const {
        data: { stories },
        total,
      } = response;

      const lastPageNumber = Math.ceil(total / perPage);
      const hasMore = page < lastPageNumber;

      setHasNextPage(hasMore);
      setTotalPages(lastPageNumber);
      setArticles(stories as ISbStoryData<Article>[]);
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      prefetchedArticlesData?.articles &&
      prefetchedArticlesData.articles.length > 0 &&
      currentPage === 1
    ) {
      usedPrefetchedData.current = true;
      return;
    }
    if (domain && version) {
      const queryToUse = options?.specificArticleType
        ? { type: { in: options.specificArticleType } }
        : undefined;
      fetchArticlesPage({
        page: currentPage,
        perPage,
        filterQuery: queryToUse,
        searchTerm: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, version, currentPage]);

  useEffect(() => {
    if (usedPrefetchedData.current && !searchQuery && currentPage === 1 && Object.keys(filterQuery).length <= 1) {
      return;
    }
    if (domain && version) {
      const queryToUse =
        options?.specificArticleType && !filterQuery?.type
          ? { ...filterQuery, type: { in: options.specificArticleType } }
          : filterQuery;
      fetchArticlesPage({
        page: currentPage,
        perPage,
        filterQuery: queryToUse,
        searchTerm: searchQuery || undefined,
      });
    }
  }, [preselectedUuids.join(","), searchQuery, currentPage, JSON.stringify(filterQuery), domain, version]);

  const hasActiveFilters = useCallback(() => {
    return Object.keys(filterValues).some((key) => {
      const value = filterValues[key];
      return value && value !== "";
    });
  }, [filterValues]);

  const filters: Filter[] = filterGroups
    .filter((group) => {
      if (options?.specificArticleType && group.key.toLowerCase() === "type") {
        return false;
      }
      return true;
    })
    .map((group) => {
      const options = group.options.map((option) => ({
        label: option.title,
        value: option.uuid,
      }));

      return {
        key: group.key,
        label: group.label,
        value: getFilterValue(group.key),
        multiselect: false,
        renderType: "select" as const,
        onValueChange: createSetValue(group.key),
        options: options,
      };
    });

  return {
    articles,
    hasNextPage,
    filters,
    searchQuery,
    onSearchChange: handleSearchChange,
    isLoading,
    currentPage,
    totalPages,
    hasActiveFilters: hasActiveFilters() || !!searchQuery,
  };
}
